// src/pages/DashboardPage.tsx — PDF Screen 4
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import {
  getDashboardStats, getDashboardFunnel, getByState,
  getRecentScans, getDailyTrend, getAlerts, askAI
} from '../services/api';
import type { FraudAlert } from '../types';
import { css } from '../utils/css';

// ── Response shapes (backend/src/controllers/dashboardController.js, aiController.js) ──
interface DashboardStats {
  total_bags: number;
  delivered: number;
  pending: number;
  fraud: number;
  expired?: number;
  delivered_today: number;
  /** Backend sends `toFixed(1)` (a string) or 0; the demo fallback uses a number. */
  delivery_rate_pct: string | number;
  unresolved_alerts?: number;
  timestamp?: string;
}
interface FunnelStage { stage: string; count: number; pct: number }
interface StateRow { state: string | null; delivered: number }
interface TrendDay { date: string; delivered: number; fraud: number }
interface RecentScan {
  scan_id: string;
  bag_id: string;
  farmer_name: string | null;
  state?: string | null;
  status: string;
  weight_kg?: number;
  scan_time: string | Date;
  is_duplicate?: boolean;
}
/** Only the alert fields this page renders (the demo fallback has no more than these). */
type AlertRow = Pick<FraudAlert, 'id' | 'type' | 'severity' | 'description'> & {
  bag?: { bagId: string; dealer?: { dealerCode: string } | null };
};
/** POST /api/ai/ask replies `{ success, answer, ai_live }` at the top level (not inside `data`). */
interface AskAIBody { success: boolean; answer?: string; ai_live?: boolean }

const TABS = ['Overview','District Map','Dealer Tracker','Fraud Alerts','Export'];
// Tabs that map to a dedicated page navigate there; null = show content in-dashboard.
const TAB_ROUTES: (string | null)[] = [null, null, '/dealer', '/alerts', '/reports'];
const POINT_COLORS = {
  FACTORY:'#1560BD', WAREHOUSE:'#E8941A', RETAILER:'#5B4FCC', FARMER:'#1A9E72'
};
const POINT_BG = {
  FACTORY:'#E8F0FD', WAREHOUSE:'#FEF3DC', RETAILER:'#EEEDFE', FARMER:'#E6F7F1'
};

function useInterval(fn: () => void, ms: number) {
  useEffect(() => {
    fn();
    const id = setInterval(fn, ms);
    return () => clearInterval(id);
  }, [ms]);
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const [tab,     setTab]     = useState(0);
  const [stats,   setStats]   = useState<DashboardStats | null>(null);
  const [funnel,  setFunnel]  = useState<FunnelStage[]>([]);
  const [states,  setStates]  = useState<StateRow[]>([]);
  const [scans,   setScans]   = useState<RecentScan[]>([]);
  const [trend,   setTrend]   = useState<TrendDay[]>([]);
  const [alerts,  setAlerts]  = useState<AlertRow[]>([]);
  const [loading, setLoading] = useState(true);

  // Ask AI
  const [aiQ,       setAiQ]       = useState('');
  const [aiAns,     setAiAns]     = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiLive,    setAiLive]    = useState(true);

  const handleAsk = async (q?: string) => {
    const question = (q ?? aiQ).trim();
    if (!question) return;
    setAiQ(question); setAiLoading(true); setAiAns('');
    try {
      const r = await askAI(question);
      const body: AskAIBody = r.data;
      setAiAns(body.answer ?? '');
      setAiLive(body.ai_live ?? false);
    } catch {
      setAiAns('Could not get a response from AI. Please try again.');
    } finally {
      setAiLoading(false);
    }
  };

  const loadStats = useCallback(async () => {
    try {
      const [s,f,st,sc,tr,al] = await Promise.all([
        getDashboardStats<DashboardStats>(), getDashboardFunnel<FunnelStage[]>(), getByState<StateRow[]>(),
        getRecentScans<RecentScan[]>(10), getDailyTrend<TrendDay[]>(), getAlerts({ limit: 5 }),
      ]);
      setStats(s.data.data);
      setFunnel(f.data.data);
      setStates(st.data.data.slice(0,6));
      setScans(sc.data.data);
      setTrend(tr.data.data);
      setAlerts(al.data.data);
    } catch {
      // Use demo data if API not running
      setStats({ total_bags:500000, delivered:372450, pending:127550, fraud:248, delivered_today:8231, delivery_rate_pct:74.5 });
      setFunnel([
        {stage:'Factory Scanned',   count:24120, pct:100},
        {stage:'Warehouse Received',count:21850, pct:90},
        {stage:'Retailer Sold',     count:18340, pct:76},
        {stage:'Farmer Received',   count:14230, pct:59},
      ]);
      setStates([
        {state:'Maharashtra',delivered:88000},{state:'UP',delivered:76000},
        {state:'Punjab',delivered:62000},{state:'Haryana',delivered:54000},
        {state:'MP',delivered:41000},{state:'Rajasthan',delivered:28000},
      ]);
      setTrend([
        {date:'Mon',delivered:12000,fraud:2},{date:'Tue',delivered:15000,fraud:3},
        {date:'Wed',delivered:11000,fraud:1},{date:'Thu',delivered:18000,fraud:4},
        {date:'Fri',delivered:14000,fraud:2},{date:'Sat',delivered:9000,fraud:1},
        {date:'Sun',delivered:8000,fraud:1},
      ]);
      setAlerts([
        {id:'1',type:'DUPLICATE_SCAN',severity:'HIGH',bag:{bagId:'URA-MH-004501'},description:'2x scanned'},
        {id:'2',type:'DUPLICATE_SCAN',severity:'HIGH',bag:{bagId:'URA-MH-003877'},description:'3x scanned'},
        {id:'3',type:'LOCATION_MISMATCH',severity:'MEDIUM',bag:{bagId:'URA-MH-002211'},description:'Dist mismatch'},
      ]);
      setScans([
        {scan_id:'1',bag_id:'URA-MH-004521',farmer_name:'Ramesh K.',scan_time:new Date(),status:'DELIVERED'},
        {scan_id:'2',bag_id:'URA-MH-004520',farmer_name:'Suresh P.',scan_time:new Date(Date.now()-8000),status:'DELIVERED'},
        {scan_id:'3',bag_id:'URA-MH-004519',farmer_name:'Priya D.',scan_time:new Date(Date.now()-12000),status:'DELIVERED'},
      ]);
    } finally {
      setLoading(false);
    }
  }, []);

  useInterval(loadStats, 30000);

  const maxState = Math.max(...states.map(s => s.delivered), 1);

  // Bag status breakdown for the donut chart
  const statusData = (() => {
    const d = stats?.delivered || 0, p = stats?.pending || 0, fr = stats?.fraud || 0;
    const other = Math.max(0, (stats?.total_bags || 0) - d - p - fr);
    return [
      { name: 'Delivered',  value: d,     color: '#1A9E72' },
      { name: 'Pending',    value: p,     color: '#E8941A' },
      { name: 'In-transit', value: other, color: '#1560BD' },
      { name: 'Fraud',      value: fr,    color: '#D94040' },
    ].filter(x => x.value > 0);
  })();
  const ago = (t: string | Date) => {
    const s = Math.floor((Date.now() - new Date(t).getTime()) / 1000);
    if (s < 60)  return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s/60)}m ago`;
    return `${Math.floor(s/3600)}h ago`;
  };

  // Sub-tab click — navigate to a page if the tab maps to one, else switch content.
  const handleTab = (i: number) => { const route = TAB_ROUTES[i]; if (route) navigate(route); else setTab(i); };

  const unresolvedAlerts = stats?.unresolved_alerts ?? 0;

  if (loading) return (
    <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', fontSize:24 }}>
      <span className="spin">⟳</span>
    </div>
  );

  return (
    <>
      {/* Topbar */}
      <div className="topbar">
        <div className="topbar-title">Live Dashboard</div>
        <div className="live-pill"><div className="live-dot"></div><span>LIVE</span></div>
        <div style={{ fontSize:11, color:'var(--ink3)', fontFamily:'DM Mono,monospace' }}>
          {new Date().toLocaleString('en-IN',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit'})}
        </div>
      </div>

      {/* Live fraud alert banner — shows the moment a 3rd scan is flagged */}
      {unresolvedAlerts > 0 && (
        <div
          onClick={() => navigate('/alerts')}
          style={{
            display:'flex', alignItems:'center', gap:12, cursor:'pointer',
            margin:'0 0 0', padding:'12px 18px',
            background:'linear-gradient(90deg,#FDECEC,#FFF5F5)',
            borderLeft:'5px solid var(--red)', borderBottom:'1px solid #F3D4D4',
          }}
        >
          <span style={{ fontSize:22, animation:'livePulse 1.5s infinite' }}>🚨</span>
          <div style={{ flex:1 }}>
            <div style={{ fontWeight:700, color:'#B02A2A', fontSize:14 }}>
              {unresolvedAlerts} New Fraud Alert{unresolvedAlerts > 1 ? 's' : ''} — Action Needed
            </div>
            <div style={{ fontSize:12, color:'#7a4d4d' }}>
              One or more bags have been flagged (3rd scan / duplicate). Click to review.
            </div>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); navigate('/alerts'); }}
            style={{ fontSize:12, fontWeight:700, color:'#fff', background:'var(--red)', border:'none', borderRadius:8, padding:'8px 16px', cursor:'pointer', whiteSpace:'nowrap' }}
          >REVIEW →</button>
        </div>
      )}

      {/* Sub-tabs (PDF Screen 4 exact) */}
      <div className="tab-scroll" style={S.tabBar}>
        {TABS.map((t,i) => (
          <div key={t} style={{...S.tab, ...(tab===i?S.tabActive:{})}} onClick={()=>handleTab(i)}>{t}</div>
        ))}
      </div>

      <div className="page-content fade-in">

        {/* ── Ask AI (Overview tab) ─────────────────────────── */}
        {tab === 0 && (
          <div style={{ marginBottom:16, background:'linear-gradient(135deg,#0f5132,#1A9E72)', borderRadius:14, padding:'16px 18px', color:'#fff', boxShadow:'0 6px 20px rgba(15,81,50,0.18)' }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
              <span style={{ fontSize:18 }}>🤖</span>
              <strong style={{ fontSize:15 }}>Ask AI</strong>
              {aiLive && (
                <span style={{ fontSize:10, fontWeight:700, background:'rgba(255,255,255,0.2)', padding:'2px 8px', borderRadius:20 }}>LIVE</span>
              )}
              <span style={{ fontSize:11, color:'rgba(255,255,255,0.75)', marginLeft:'auto' }}>Ask about the data in plain English</span>
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <input
                value={aiQ}
                onChange={e=>setAiQ(e.target.value)}
                onKeyDown={e=>{ if(e.key==='Enter') handleAsk(); }}
                placeholder="e.g. How many bags were delivered today? Which district has the most fraud?"
                style={{ flex:1, border:'none', borderRadius:9, padding:'10px 12px', fontSize:13, outline:'none', color:'#111' }}
              />
              <button onClick={()=>handleAsk()} disabled={aiLoading}
                style={{ background:'#fff', color:'#0f5132', border:'none', borderRadius:9, padding:'10px 16px', fontWeight:700, fontSize:13, cursor:'pointer', whiteSpace:'nowrap' }}>
                {aiLoading ? '…' : 'Ask'}
              </button>
            </div>
            <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginTop:8 }}>
              {['How many bags were delivered today?','Which district has the most deliveries?','How many fraud alerts are pending?','What is the delivery rate?','How many bags are still pending?','Which states have the highest deliveries?','How many fraud cases in total?'].map(s => (
                <span key={s} onClick={()=>handleAsk(s)}
                  style={{ fontSize:11, background:'rgba(255,255,255,0.15)', border:'1px solid rgba(255,255,255,0.25)', padding:'4px 10px', borderRadius:20, cursor:'pointer' }}>{s}</span>
              ))}
            </div>
            {(aiLoading || aiAns) && (
              <div style={{ marginTop:12, background:'rgba(255,255,255,0.95)', color:'#111', borderRadius:10, padding:'12px 14px', fontSize:13, lineHeight:1.6, whiteSpace:'pre-wrap' }}>
                {aiLoading ? <span className="spin">⟳</span> : aiAns}
              </div>
            )}
          </div>
        )}

        {/* KPI Cards — PDF exact */}
        <div className="grid-4" style={{ marginBottom:16 }}>
          {[
            { label:'Total Bags Issued', icon:'📦', val: stats?.total_bags?.toLocaleString('en-IN'), sub:'This season', color:'var(--g2)', bar:'linear-gradient(90deg,var(--g3),var(--g4))', tint:'linear-gradient(135deg,#EFFAF4,#fff 72%)', chip:'#E3F6EC' },
            { label:'Delivered', icon:'✅', val: stats?.delivered?.toLocaleString('en-IN'), sub:`${stats?.delivery_rate_pct}% completion`, color:'var(--g2)', bar:'linear-gradient(90deg,var(--g3),var(--g4))', tint:'linear-gradient(135deg,#EFFAF4,#fff 72%)', chip:'#E3F6EC' },
            { label:'Pending', icon:'⏳', val: stats?.pending?.toLocaleString('en-IN'), sub:`${stats?.total_bags ? ((stats.pending/stats.total_bags)*100).toFixed(1) : 0}% unscanned`, color:'var(--amb)', bar:'linear-gradient(90deg,var(--amb),#FFCA6A)', tint:'linear-gradient(135deg,#FEF6E7,#fff 72%)', chip:'#FCEECF' },
            { label:'Fraud Alerts', icon:'🚨', val: stats?.fraud?.toLocaleString('en-IN'), sub:'Duplicate scans flagged', color:'var(--red)', bar:'linear-gradient(90deg,var(--red),#FF8A8A)', tint:'linear-gradient(135deg,#FDEEEE,#fff 72%)', chip:'#FADEDE' },
          ].map(k => (
            <div key={k.label} className="card" style={{ position:'relative', overflow:'hidden', cursor:'default', background:k.tint }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10 }}>
                <div style={{ fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', color:'var(--ink3)' }}>{k.label}</div>
                <div style={{ width:30, height:30, borderRadius:9, background:k.chip, display:'flex', alignItems:'center', justifyContent:'center', fontSize:15 }}>{k.icon}</div>
              </div>
              <div style={{ fontSize:30, fontWeight:800, color:k.color, lineHeight:1, marginBottom:5 }}>{k.val || '—'}</div>
              <div style={{ fontSize:11, color:'var(--ink3)' }}>{k.sub}</div>
              <div style={{ position:'absolute', bottom:0, left:0, right:0, height:3, background:k.bar, borderRadius:'0 0 16px 16px' }}></div>
            </div>
          ))}
        </div>

        {/* ── Overview tab content ── */}
        {tab === 0 && (<>
        {/* 4 Scan Point Counters (NEW — all points live) */}
        <div className="grid-4" style={{ marginBottom:16 }}>
          {[
            { point:'Factory',   icon:'🏭', color:'var(--blue)',  bg:'var(--bluep)',  count: funnel[0]?.count },
            { point:'Warehouse', icon:'🏪', color:'var(--amb)',   bg:'var(--ambp)',   count: funnel[1]?.count },
            { point:'Retailer',  icon:'🏬', color:'var(--purp)',  bg:'var(--purpp)',  count: funnel[2]?.count },
            { point:'Farmer',    icon:'👨‍🌾', color:'var(--g3)',   bg:'var(--gp)',     count: funnel[3]?.count },
          ].map(sp => (
            <div key={sp.point} className="card" style={{ position:'relative', overflow:'hidden' }}>
              <div style={{ position:'absolute', top:0, left:0, bottom:0, width:4, background:sp.color, borderRadius:'12px 0 0 12px' }}></div>
              <div style={{ paddingLeft:10 }}>
                <div style={{ fontSize:18, marginBottom:4 }}>{sp.icon}</div>
                <div style={{ fontSize:9, fontWeight:700, letterSpacing:'0.08em', color:'var(--ink3)', textTransform:'uppercase', marginBottom:3 }}>{sp.point} Scan</div>
                <div style={{ fontSize:22, fontWeight:700, color:sp.color, lineHeight:1 }}>{sp.count?.toLocaleString('en-IN') || '—'}</div>
                <div style={{ fontSize:10, color:'var(--ink3)', marginTop:2 }}>bags today</div>
              </div>
            </div>
          ))}
        </div>

        {/* Mid row */}
        <div className="split-main" style={{ display:'grid', gridTemplateColumns:'1fr 300px', gap:14, marginBottom:14 }}>

          {/* District Progress (PDF Screen 4 exact) */}
          <div className="card">
            <div className="card-title">District Progress — Bags delivered %</div>
            {states.map((s, i) => {
              const pct = Math.round((s.delivered / maxState) * 100);
              return (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
                  <div style={{ fontSize:12, fontWeight:500, color:'var(--ink2)', width:100, flexShrink:0 }}>{s.state}</div>
                  <div style={{ flex:1, height:8, background:'var(--bg)', borderRadius:8, overflow:'hidden', border:'1px solid var(--bor)' }}>
                    <div style={{ height:'100%', width:`${pct}%`, background:'linear-gradient(90deg,var(--g3),var(--g4))', borderRadius:8, transition:'width 1s' }}></div>
                  </div>
                  <div style={{ fontSize:11, fontWeight:600, color:'var(--g2)', width:32, textAlign:'right' }}>{pct}%</div>
                </div>
              );
            })}
          </div>

          {/* Live Activity Feed (PDF Screen 4 exact) */}
          <div className="card" style={{ padding:0, overflow:'hidden' }}>
            <div style={{ padding:'14px 16px', borderBottom:'1px solid var(--bor)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <div className="card-title" style={{ marginBottom:0 }}>Live Activity Feed</div>
              <div className="live-pill"><div className="live-dot"></div><span>LIVE</span></div>
            </div>
            <div style={{ maxHeight:220, overflowY:'auto' }}>
              {scans.map((sc, i) => (
                <div key={sc.scan_id||i} style={{ display:'flex', alignItems:'center', gap:8, padding:'9px 16px', borderBottom:'1px solid var(--gpp)', fontSize:12, transition:'background .1s' }}>
                  <div style={{ width:6, height:6, borderRadius:'50%', background:'var(--g4)', flexShrink:0 }}></div>
                  <div style={{ fontFamily:'DM Mono,monospace', fontSize:11, color:'var(--ink)', flex:1 }}>{sc.bag_id}</div>
                  <div style={{ fontSize:10, color:'var(--ink3)' }}>{sc.farmer_name}</div>
                  <div style={{ fontSize:10, color:'var(--ink3)', fontFamily:'DM Mono,monospace', flexShrink:0 }}>{ago(sc.scan_time)}</div>
                </div>
              ))}
              {scans.length === 0 && <div style={{ padding:20, textAlign:'center', color:'var(--ink3)', fontSize:12 }}>No recent scans</div>}
            </div>
          </div>
        </div>

        {/* ── Analytics charts (easy visual analysis) ── */}
        <div className="grid-2" style={{ marginBottom:14 }}>
          {/* Donut — bag status distribution */}
          <div className="card">
            <div className="card-title">Bag Status Distribution</div>
            <ResponsiveContainer width="100%" height={210}>
              <PieChart>
                <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={48} outerRadius={74} paddingAngle={2}>
                  {statusData.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Pie>
                <Tooltip formatter={(v) => v.toLocaleString('en-IN')} contentStyle={{ fontSize:11, borderRadius:8, border:'1px solid #D0E0D8' }} />
                <Legend wrapperStyle={{ fontSize:11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Horizontal bar — top states by delivery */}
          <div className="card">
            <div className="card-title">Top States — Bags Delivered</div>
            <ResponsiveContainer width="100%" height={210}>
              <BarChart data={states} layout="vertical" margin={{ left: 8, right: 14, top: 4, bottom: 4 }}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="state" tick={{ fontSize:10, fill:'#7A8A7E' }} width={80} axisLine={false} tickLine={false} />
                <Tooltip formatter={(v) => v.toLocaleString('en-IN')} contentStyle={{ fontSize:11, borderRadius:8, border:'1px solid #D0E0D8' }} />
                <Bar dataKey="delivered" fill="#1A9E72" radius={[0,4,4,0]} barSize={16} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Bottom row */}
        <div className="grid-2">

          {/* 7-day trend chart */}
          <div className="card">
            <div className="card-title">Daily Scan Trend — Last 7 Days</div>
            <ResponsiveContainer width="100%" height={140}>
              <BarChart data={trend} barGap={4}>
                <XAxis dataKey="date" tick={{ fontSize:10, fill:'#7A8A7E' }} axisLine={false} tickLine={false} />
                <YAxis hide />
                <Tooltip contentStyle={{ fontSize:11, borderRadius:8, border:'1px solid #D0E0D8' }} />
                <Bar dataKey="delivered" fill="#1A9E72" radius={[4,4,0,0]} name="Delivered" />
                <Bar dataKey="fraud"     fill="#FDEAEA" radius={[4,4,0,0]} name="Fraud" stroke="#D94040" strokeWidth={0.5} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Fraud table (PDF Screen 4 exact) */}
          <div className="card" style={{ padding:0, overflow:'hidden' }}>
            <div style={{ padding:'14px 16px', borderBottom:'1px solid var(--bor)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div className="card-title" style={{ marginBottom:0 }}>🚨 Fraud / Duplicate Scan Alerts</div>
            </div>
            <div className="tbl-scroll">
            <table className="tbl">
              <thead>
                <tr><th>Bag ID</th><th>Dealer</th><th>Reason</th></tr>
              </thead>
              <tbody>
                {alerts.map(a => (
                  <tr key={a.id}>
                    <td className="mono" style={{ fontSize:11 }}>{a.bag?.bagId || '—'}</td>
                    <td style={{ fontSize:11, color:'var(--ink3)' }}>{a.bag?.dealer?.dealerCode || '—'}</td>
                    <td><span className={`tag ${a.type==='DUPLICATE_SCAN'?'tag-red':'tag-amber'}`}>{a.description||a.type}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            <div style={{ padding:'10px 16px', fontSize:11, color:'var(--blue)', fontWeight:600, cursor:'pointer' }}>
              VIEW ALL {stats?.fraud} ALERTS →
            </div>
          </div>
        </div>

        {/* Supply chain funnel */}
        <div className="card" style={{ marginTop:14 }}>
          <div className="card-title">Supply Chain Funnel</div>
          <div className="r-grid-4" style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12 }}>
            {funnel.map((f,i) => (
              <div key={i}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                  <span style={{ fontSize:11, fontWeight:600, color:'var(--ink2)' }}>{f.stage}</span>
                  <span style={{ fontSize:11, fontFamily:'DM Mono,monospace', color:'var(--ink2)', fontWeight:500 }}>{f.count?.toLocaleString('en-IN')}</span>
                </div>
                <div style={{ height:8, background:'var(--bg)', borderRadius:8, overflow:'hidden', border:'1px solid var(--bor)' }}>
                  <div style={{ height:'100%', width:`${f.pct}%`, background:['var(--blue)','var(--amb)','var(--purp)','var(--g3)'][i], borderRadius:8, transition:'width 1s' }}></div>
                </div>
              </div>
            ))}
          </div>
        </div>
        </>)}

        {/* ── District Map tab ── */}
        {tab === 1 && (
          <>
            <div className="card">
              <div className="card-title">District / State Progress — Bags Delivered</div>
              {states.map((s, i) => { const pct = Math.round((s.delivered / maxState) * 100); return (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:12, marginBottom:12 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:'var(--ink2)', width:150, flexShrink:0 }}>{s.state}</div>
                  <div style={{ flex:1, height:10, background:'var(--bg)', borderRadius:8, overflow:'hidden', border:'1px solid var(--bor)' }}><div style={{ height:'100%', width:`${pct}%`, background:'linear-gradient(90deg,var(--g3),var(--g4))', borderRadius:8, transition:'width 1s' }}></div></div>
                  <div style={{ fontSize:12, fontWeight:700, color:'var(--g2)', width:64, textAlign:'right' }}>{s.delivered?.toLocaleString('en-IN')}</div>
                </div>
              ); })}
              {states.length === 0 && <div style={{ padding:20, textAlign:'center', color:'var(--ink3)', fontSize:12 }}>No district data yet</div>}
            </div>
            <div className="card" style={{ marginTop:14 }}>
              <div className="card-title">Top States — Bags Delivered</div>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={states} layout="vertical" margin={{ left: 8, right: 16, top: 4, bottom: 4 }}>
                  <XAxis type="number" hide />
                  <YAxis type="category" dataKey="state" tick={{ fontSize:11, fill:'#7A8A7E' }} width={95} axisLine={false} tickLine={false} />
                  <Tooltip formatter={(v) => v.toLocaleString('en-IN')} contentStyle={{ fontSize:11, borderRadius:8, border:'1px solid #D0E0D8' }} />
                  <Bar dataKey="delivered" fill="#1A9E72" radius={[0,4,4,0]} barSize={18} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </>
        )}

      </div>
    </>
  );
}

const S = css({
  tabBar:  { display:'flex', gap:0, background:'var(--card)', borderBottom:'1px solid var(--bor)', padding:'0 24px', flexShrink:0 },
  tab:     { padding:'11px 16px', fontSize:12, fontWeight:500, color:'var(--ink3)', cursor:'pointer', borderBottom:'2px solid transparent', transition:'all .15s', whiteSpace:'nowrap' },
  tabActive:{ color:'var(--g2)', borderBottomColor:'var(--g3)', fontWeight:600 },
});
